import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select } from "../ui/select";
import { Loader2, Trash2, Plus, Users, PhoneCall, GitBranch, ClipboardCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { previewAudience, createCampaign, fetchRetellAgents, fetchRetellPhoneNumbers } from "../../services/crmServices/campaignService";
import { fetchDistinctValues } from "../../services/crmServices/leadService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { fetchDesks } from "../../services/crmServices/deskService";
import { fetchAvailableUsers } from "../../services/crmServices/userService";

const DYNAMIC_FIELDS = [
    "firstName", "lastName", "email", "phone", "secondPhone",
    "status", "source", "affiliate", "country", "city", "address",
    "extra", "balance", "balanceCurrency"
];

const STEPS = [
    { id: 1, label: "Audience", icon: Users },
    { id: 2, label: "Call Setup", icon: PhoneCall },
    { id: 3, label: "Outcomes", icon: GitBranch },
    { id: 4, label: "Review", icon: ClipboardCheck }
];

function MultiSelectChips({ options, selected, onToggle, emptyText }) {
    if (!options.length) {
        return <p className="text-xs text-gray-400">{emptyText || "No options available"}</p>;
    }
    return (
        <div className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto">
            {options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onToggle(opt.value)}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                            isSelected
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

export function CreateCampaignModal({ open, onClose, onCreated }) {
    const [step, setStep] = useState(1);
    const [creating, setCreating] = useState(false);

    // Step 1: audience
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [statuses, setStatuses] = useState([]);
    const [sources, setSources] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [desks, setDesks] = useState([]);
    const [rangeFrom, setRangeFrom] = useState("");
    const [rangeTo, setRangeTo] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Options
    const [statusOptions, setStatusOptions] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [affiliateOptions, setAffiliateOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [deskOptions, setDeskOptions] = useState([]);

    // Step 2: call setup
    const [agents, setAgents] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [retellLoading, setRetellLoading] = useState(false);
    const [retellError, setRetellError] = useState("");
    const [agentId, setAgentId] = useState("");
    const [fromNumber, setFromNumber] = useState("");
    const [transferNumber, setTransferNumber] = useState("");
    const [transferAgentId, setTransferAgentId] = useState("");
    const [crmAgents, setCrmAgents] = useState([]);
    const [concurrency, setConcurrency] = useState(5);
    const [dynamicFields, setDynamicFields] = useState(["firstName", "lastName", "phone", "email"]);

    // Step 3: outcome mapping
    const [outcomeRules, setOutcomeRules] = useState([]);
    const [defaultStatus, setDefaultStatus] = useState("");

    const buildFilters = useCallback(() => {
        const filters = {};
        if (statuses.length) filters.statuses = statuses;
        if (sources.length) filters.source = sources;
        if (affiliates.length) filters.affiliate = affiliates;
        if (countries.length) filters.country = countries;
        if (desks.length) filters.desk = desks;
        return filters;
    }, [statuses, sources, affiliates, countries, desks]);

    // Load filter options when opened
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [statusRes, sourceRes, affiliateRes, countryRes, deskRes] = await Promise.all([
                    fetchLeadStatuses(),
                    fetchDistinctValues("source"),
                    fetchDistinctValues("affiliate"),
                    fetchDistinctValues("country"),
                    fetchDesks()
                ]);
                setStatusOptions((statusRes?.data || []).map((s) => ({ label: s.label || s.value, value: s.value || s.label })));
                setSourceOptions((sourceRes?.data || []).filter(Boolean).map((s) => ({ label: s, value: s })));
                setAffiliateOptions((affiliateRes?.data || []).filter(Boolean).map((a) => ({ label: a, value: a })));
                setCountryOptions((countryRes?.data || []).filter(Boolean).map((c) => ({ label: c, value: c })));
                setDeskOptions((deskRes?.data || []).map((d) => ({ label: d.name, value: d._id })));
            } catch (err) {
                console.error("Failed to load filter options", err);
            }
        })();
    }, [open]);

    const buildRange = useCallback(() => {
        const from = parseInt(rangeFrom, 10);
        const to = parseInt(rangeTo, 10);
        if (isNaN(from) && isNaN(to)) return null;
        return { from: isNaN(from) ? undefined : from, to: isNaN(to) ? undefined : to };
    }, [rangeFrom, rangeTo]);

    // Live audience preview
    useEffect(() => {
        if (!open) return;
        setPreviewLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await previewAudience(buildFilters(), buildRange());
                setPreview(res?.data || null);
            } catch (err) {
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [open, buildFilters, buildRange]);

    // Load Retell agents/numbers when reaching step 2
    useEffect(() => {
        if (!open || step !== 2 || agents.length > 0) return;
        (async () => {
            setRetellLoading(true);
            setRetellError("");
            try {
                const [agentsRes, numbersRes] = await Promise.all([
                    fetchRetellAgents(),
                    fetchRetellPhoneNumbers()
                ]);
                setAgents(Array.isArray(agentsRes?.data) ? agentsRes.data : []);
                setPhoneNumbers(Array.isArray(numbersRes?.data) ? numbersRes.data : []);
            } catch (err) {
                setRetellError(err?.response?.data?.message || "Failed to load Retell agents. Check RetellAI settings.");
            } finally {
                setRetellLoading(false);
            }
            // CRM agents who can receive live transfers (screen-pop recipients)
            try {
                const usersRes = await fetchAvailableUsers();
                setCrmAgents(Array.isArray(usersRes?.data) ? usersRes.data : []);
            } catch (err) {
                setCrmAgents([]);
            }
        })();
    }, [open, step, agents.length]);

    const resetAll = () => {
        setStep(1);
        setName("");
        setDescription("");
        setStatuses([]);
        setSources([]);
        setAffiliates([]);
        setCountries([]);
        setDesks([]);
        setRangeFrom("");
        setRangeTo("");
        setPreview(null);
        setAgentId("");
        setFromNumber("");
        setTransferNumber("");
        setTransferAgentId("");
        setCrmAgents([]);
        setConcurrency(5);
        setDynamicFields(["firstName", "lastName", "phone", "email"]);
        setOutcomeRules([]);
        setDefaultStatus("");
        setAgents([]);
        setPhoneNumbers([]);
        setRetellError("");
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const toggleIn = (list, setList) => (value) => {
        setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    };

    const addOutcomeRule = () => {
        setOutcomeRules([...outcomeRules, { source: "call_successful", key: "", value: "true", newStatus: "" }]);
    };

    const updateOutcomeRule = (index, patch) => {
        setOutcomeRules(outcomeRules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const removeOutcomeRule = (index) => {
        setOutcomeRules(outcomeRules.filter((_, i) => i !== index));
    };

    const canProceedFromStep1 = name.trim() && preview && preview.withPhone > 0;
    const canProceedFromStep2 = agentId && fromNumber;

    const handleCreate = async () => {
        setCreating(true);
        try {
            const res = await createCampaign({
                name: name.trim(),
                description,
                audienceFilters: buildFilters(),
                audienceRange: buildRange(),
                retellAgentId: agentId,
                retellFromNumber: fromNumber,
                transferNumber: transferNumber.trim(),
                transferAgentId: transferAgentId || null,
                concurrencyLimit: concurrency,
                dynamicVariableFields: dynamicFields,
                outcomeMapping: outcomeRules.filter((r) => r.newStatus),
                defaultPostCallStatus: defaultStatus
            });
            if (res?.data) {
                toast.success("Campaign created");
                handleClose();
                onCreated?.(res.data);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to create campaign");
        } finally {
            setCreating(false);
        }
    };

    const agentOptions = agents.map((a) => ({
        label: `${a.agent_name || "Unnamed agent"} (${a.agent_id})`,
        value: a.agent_id
    }));
    const numberOptions = phoneNumbers.map((n) => ({
        label: `${n.phone_number_pretty || n.phone_number}${n.nickname ? ` — ${n.nickname}` : ""}`,
        value: n.phone_number
    }));
    const transferAgentOptions = [
        { label: "— None (no live transfer) —", value: "" },
        ...crmAgents.map((a) => ({
            label: `${a.firstName || ""} ${a.lastName || ""}`.trim() + (a.phone ? ` — ${a.phone}` : " — (no phone)"),
            value: String(a._id)
        }))
    ];
    const statusSelectOptions = [{ label: "— Leave unchanged —", value: "" }, ...statusOptions];

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>New AI Call Campaign</DialogTitle>
                    <DialogDescription>
                        Select leads, choose a Retell agent, and map call outcomes to lead statuses.
                    </DialogDescription>
                </DialogHeader>

                {/* Step indicator */}
                <div className="flex items-center gap-2 mb-2">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-2">
                            <div
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                    step === s.id
                                        ? "bg-indigo-600 text-white"
                                        : step > s.id
                                            ? "bg-indigo-100 text-indigo-700"
                                            : "bg-gray-100 text-gray-400"
                                }`}
                            >
                                <s.icon className="h-3 w-3" />
                                {s.label}
                            </div>
                            {i < STEPS.length - 1 && <div className="w-4 h-px bg-gray-200" />}
                        </div>
                    ))}
                </div>

                {/* Step 1 — Audience */}
                {step === 1 && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Campaign Name *</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reactivate old leads" className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Description (optional)</Label>
                                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Internal note" className="mt-1" />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Lead Status</Label>
                            <MultiSelectChips options={statusOptions} selected={statuses} onToggle={toggleIn(statuses, setStatuses)} emptyText="No statuses defined" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Source</Label>
                            <MultiSelectChips options={sourceOptions} selected={sources} onToggle={toggleIn(sources, setSources)} emptyText="No sources found" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Affiliate</Label>
                            <MultiSelectChips options={affiliateOptions} selected={affiliates} onToggle={toggleIn(affiliates, setAffiliates)} emptyText="No affiliates found" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Country</Label>
                            <MultiSelectChips options={countryOptions} selected={countries} onToggle={toggleIn(countries, setCountries)} emptyText="No countries found" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Desk</Label>
                            <MultiSelectChips options={deskOptions} selected={desks} onToggle={toggleIn(desks, setDesks)} emptyText="No desks found" />
                        </div>

                        {/* Lead range selection */}
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Lead Range (optional)</Label>
                            <p className="text-[11px] text-gray-500 mb-1">
                                Take only part of the matched list, e.g. leads 1–500 or 700–1400. Leads are ordered by last update (newest first). Leave empty to use all.
                            </p>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    value={rangeFrom}
                                    onChange={(e) => setRangeFrom(e.target.value)}
                                    placeholder="From (e.g. 1)"
                                    className="w-36"
                                />
                                <span className="text-gray-400 text-sm">—</span>
                                <Input
                                    type="number"
                                    min="1"
                                    value={rangeTo}
                                    onChange={(e) => setRangeTo(e.target.value)}
                                    placeholder={`To (max ${preview?.total ?? "…"})`}
                                    className="w-36"
                                />
                                {(rangeFrom || rangeTo) && (
                                    <Button variant="ghost" size="sm" onClick={() => { setRangeFrom(""); setRangeTo(""); }}>
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-lg flex items-center gap-2">
                            {previewLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                            ) : (
                                <Users className="h-4 w-4 text-indigo-600" />
                            )}
                            <p className="text-sm text-indigo-800">
                                {preview
                                    ? (preview.inRange !== undefined && preview.inRange !== preview.total
                                        ? <><span className="font-semibold">{preview.withPhone}</span> callable leads in selected range of <span className="font-semibold">{preview.inRange}</span> (matched {preview.total} total; leads without a phone are skipped)</>
                                        : <><span className="font-semibold">{preview.withPhone}</span> callable leads match (of {preview.total} total; leads without a phone are skipped)</>)
                                    : "Select filters to preview the audience. No filters = all leads you can access."}
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 2 — Call setup */}
                {step === 2 && (
                    <div className="space-y-4">
                        {retellLoading && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading Retell agents and numbers…
                            </div>
                        )}
                        {retellError && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{retellError}</div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Retell Agent *</Label>
                                <Select
                                    options={agentOptions}
                                    value={agentId}
                                    onChange={setAgentId}
                                    placeholder="Select agent"
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">From Number *</Label>
                                <Select
                                    options={numberOptions}
                                    value={fromNumber}
                                    onChange={setFromNumber}
                                    placeholder="Select number"
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                            <Label className="text-xs font-semibold text-gray-700">Live Transfer (optional)</Label>
                            <p className="text-[11px] text-gray-500 mt-0.5 mb-2">When a customer wants to speak to an expert now, the call is transferred to this agent and their CRM screen pops up with the customer's details.</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] font-medium text-gray-600">Receiving Agent</Label>
                                    <Select
                                        options={transferAgentOptions}
                                        value={transferAgentId}
                                        onChange={(val) => {
                                            setTransferAgentId(val);
                                            const u = crmAgents.find((a) => String(a._id) === String(val));
                                            if (u?.phone) setTransferNumber(u.phone);
                                        }}
                                        placeholder="Select agent"
                                        searchable
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px] font-medium text-gray-600">Transfer Number</Label>
                                    <Input
                                        type="tel"
                                        value={transferNumber}
                                        onChange={(e) => setTransferNumber(e.target.value)}
                                        placeholder="+441234567890"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            <p className="text-[11px] text-gray-400 mt-1">The number is auto-filled from the agent's profile — edit it if their desk line differs. Leave both blank to disable live transfer.</p>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Concurrent Calls (1–50)</Label>
                            <Input
                                type="number"
                                min="1"
                                max="50"
                                value={concurrency}
                                onChange={(e) => setConcurrency(Math.min(50, Math.max(1, parseInt(e.target.value || "5", 10))))}
                                className="mt-1 w-32"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">How many leads are called at the same time. Must fit within your Retell workspace concurrency limit.</p>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Lead Fields Passed to the AI Agent</Label>
                            <p className="text-[11px] text-gray-500 mb-1">Available in the agent prompt as dynamic variables, e.g. {"{{firstName}}"}</p>
                            <MultiSelectChips
                                options={DYNAMIC_FIELDS.map((f) => ({ label: f, value: f }))}
                                selected={dynamicFields}
                                onToggle={toggleIn(dynamicFields, setDynamicFields)}
                            />
                        </div>
                    </div>
                )}

                {/* Step 3 — Outcome mapping */}
                {step === 3 && (
                    <div className="space-y-4">
                        <p className="text-xs text-gray-500">
                            When a call finishes, the first matching rule sets the lead's new status. Rules check the call result from Retell
                            (disconnection reason, the analyzed "call successful" flag, or a custom analysis field from your agent).
                        </p>

                        {outcomeRules.map((rule, index) => (
                            <div key={index} className="p-3 border border-gray-200 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-600">Rule {index + 1}</span>
                                    <Button variant="ghost" size="sm" onClick={() => removeOutcomeRule(index)}>
                                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                    <Select
                                        options={[
                                            { label: "Call successful (true/false)", value: "call_successful" },
                                            { label: "Disconnection reason", value: "disconnection_reason" },
                                            { label: "Reached voicemail (true/false)", value: "in_voicemail" },
                                            { label: "User sentiment", value: "user_sentiment" },
                                            { label: "Custom analysis field", value: "custom_analysis_data" }
                                        ]}
                                        value={rule.source}
                                        onChange={(v) => updateOutcomeRule(index, { source: v })}
                                        placeholder="Check..."
                                    />
                                    {rule.source === "custom_analysis_data" && (
                                        <Input
                                            value={rule.key}
                                            onChange={(e) => updateOutcomeRule(index, { key: e.target.value })}
                                            placeholder="Field name (e.g. interested)"
                                        />
                                    )}
                                    <Input
                                        value={rule.value}
                                        onChange={(e) => updateOutcomeRule(index, { value: e.target.value })}
                                        placeholder={rule.source === "disconnection_reason" ? "e.g. voicemail_reached, dial_no_answer (comma = any of)" : "e.g. true"}
                                    />
                                    <Select
                                        options={statusOptions}
                                        value={rule.newStatus}
                                        onChange={(v) => updateOutcomeRule(index, { newStatus: v })}
                                        placeholder="Set status to..."
                                        searchable
                                    />
                                </div>
                            </div>
                        ))}

                        <Button variant="outline" size="sm" onClick={addOutcomeRule}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add Rule
                        </Button>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Default Status (if no rule matches)</Label>
                            <Select
                                options={statusSelectOptions}
                                value={defaultStatus}
                                onChange={setDefaultStatus}
                                placeholder="— Leave unchanged —"
                                searchable
                                className="mt-1 max-w-sm"
                            />
                        </div>
                    </div>
                )}

                {/* Step 4 — Review */}
                {step === 4 && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Campaign</p>
                                <p className="font-medium text-gray-900">{name}</p>
                                {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Audience</p>
                                <p className="font-medium text-gray-900">
                                    {preview?.withPhone ?? "?"} callable leads
                                    {(rangeFrom || rangeTo) && <span className="text-xs text-gray-500 font-normal"> (range {rangeFrom || 1}–{rangeTo || preview?.total || "end"})</span>}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {statuses.map((s) => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                                    {sources.map((s) => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                                    {affiliates.map((a) => <Badge key={a} variant="outline" className="text-xs">{a}</Badge>)}
                                    {countries.map((c) => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}
                                </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Call Setup</p>
                                <p className="text-gray-900">Agent: <span className="font-mono text-xs">{agentId}</span></p>
                                <p className="text-gray-900">From: {fromNumber}</p>
                                {transferNumber.trim() && (
                                    <p className="text-gray-900">
                                        Transfer to: {transferNumber.trim()}
                                        {(() => {
                                            const u = crmAgents.find((a) => String(a._id) === String(transferAgentId));
                                            return u ? ` (${`${u.firstName || ""} ${u.lastName || ""}`.trim()})` : "";
                                        })()}
                                    </p>
                                )}
                                <p className="text-gray-900">Concurrency: {concurrency}</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 mb-1">Outcome Rules</p>
                                {outcomeRules.filter((r) => r.newStatus).length === 0 && !defaultStatus ? (
                                    <p className="text-xs text-gray-500">Lead statuses will not be changed</p>
                                ) : (
                                    <>
                                        {outcomeRules.filter((r) => r.newStatus).map((r, i) => (
                                            <p key={i} className="text-xs text-gray-700">
                                                {r.source}{r.key ? `.${r.key}` : ""} = "{r.value}" → <span className="font-medium">{r.newStatus}</span>
                                            </p>
                                        ))}
                                        {defaultStatus && <p className="text-xs text-gray-700">Otherwise → <span className="font-medium">{defaultStatus}</span></p>}
                                    </>
                                )}
                            </div>
                        </div>
                        <p className="text-xs text-gray-500">
                            The campaign is created in <span className="font-medium">Draft</span> — you start it from the campaign list.
                        </p>
                    </div>
                )}

                {/* Footer nav */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Button variant="outline" onClick={step === 1 ? handleClose : () => setStep(step - 1)}>
                        {step === 1 ? "Cancel" : <><ChevronLeft className="h-4 w-4 mr-1" /> Back</>}
                    </Button>
                    {step < 4 ? (
                        <Button
                            onClick={() => setStep(step + 1)}
                            disabled={(step === 1 && !canProceedFromStep1) || (step === 2 && !canProceedFromStep2)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            Next <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleCreate}
                            disabled={creating}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}
                            Create Campaign
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
